import { ArrowUpRight, ArrowDownLeft, Users, Clock } from "lucide-react";
import { Card } from "./ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { useUserProfile } from "./UserProfileContext";
import { formatCurrencyForRegion } from "../utils/regions";
import { Badge } from "./ui/badge";
import { formatDate } from "../utils/formatDate";
import type {
  TransactionType,
  TransactionStatus,
} from "../shared/transactions";

interface TransactionUser {
  name: string;
  avatar?: string;
}

interface Transaction {
  id: string;
  type: TransactionType;
  amount: number;
  description: string;
  user?: TransactionUser; // Legacy format
  recipient?: TransactionUser; // New format
  sender?: TransactionUser; // New format
  avatarFallback?: string; // Fallback avatar text
  date: string;
  status: TransactionStatus;
}

interface TransactionCardProps {
  transaction: Transaction;
  onNavigate?: (tab: string, data?: any) => void;
  onClick?: () => void;
  currencySymbol?: string; // deprecated, left for backward compat
  /** Optional extra label shown under the type label on the right */
  rightSubLabel?: string;
  /** Optional plain text shown on bottom-right row next to badges */
  extraRightText?: string;
}

export function TransactionCard({
  transaction,
  onNavigate,
  onClick,
  rightSubLabel,
  extraRightText,
}: TransactionCardProps) {
  const { appSettings } = useUserProfile();
  const fmt = (n: number) => formatCurrencyForRegion(appSettings.region, n);
  const getIcon = () => {
    switch (transaction.type) {
      case "sent":
        return <ArrowUpRight className="h-4 w-4 text-destructive" />;
      case "received":
        return <ArrowDownLeft className="h-4 w-4 text-success" />;
      case "split":
      case "bill_split":
        return <Users className="h-4 w-4 text-primary" />;
      case "request":
        return <Clock className="h-4 w-4 text-warning" />;
      default:
        return null;
    }
  };

  const getAmountColor = () => {
    switch (transaction.type) {
      case "sent":
        return "text-destructive";
      case "received":
        return "text-success";
      case "split":
      case "bill_split":
        return "text-primary";
      case "request":
        return "text-warning";
      default:
        return "text-foreground";
    }
  };

  const getAmountPrefix = () => {
    switch (transaction.type) {
      case "sent":
        return "-";
      case "received":
        return "+";
      case "split":
      case "bill_split":
        return "-";
      case "request":
        return "";
      default:
        return "";
    }
  };

  const getTypeLabel = () => {
    switch (transaction.type) {
      case "sent":
        return "Sent";
      case "received":
        return "Received";
      case "split":
      case "bill_split":
        return "Bill Split";
      case "request":
        return "Request";
      default:
        return "";
    }
  };

  // Determine user info based on transaction type and available data
  const getUserInfo = () => {
    // Legacy format
    if (transaction.user) {
      return transaction.user;
    }

    // New format - determine user based on transaction type
    if (transaction.type === "sent" && transaction.recipient) {
      return transaction.recipient;
    }

    if (transaction.type === "received" && transaction.sender) {
      return transaction.sender;
    }

    if (
      (transaction.type === "bill_split" || transaction.type === "split") &&
      transaction.recipient
    ) {
      return transaction.recipient;
    }

    if (transaction.type === "request" && transaction.recipient) {
      return transaction.recipient;
    }

    // Fallback
    return {
      name: transaction.type === "bill_split" ? "Bill Split" : "Unknown",
    };
  };

  const getInitials = (name?: string) =>
    String(name || "")
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const userInfo = getUserInfo();
  const safeName =
    userInfo?.name ||
    (transaction.type === "bill_split" ? "Bill Split" : "Unknown");
  const avatarFallback = transaction.avatarFallback || getInitials(safeName);

  // Prefer a meaningful subtitle for requests; fall back to generic label
  const getSubtitle = () => {
    if (transaction.type === "request") {
      const msg = (transaction.description || "").trim();
      return msg.length > 0 ? msg : "Payment request";
    }
    return transaction.description;
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
      return;
    }
    if (onNavigate) {
      // Always open details from history to avoid confusing direct payment on requests.
      // Pay actions are gated via Upcoming Payments and dedicated flows only.
      onNavigate("transaction-details", { transactionId: transaction.id });
    }
  };

  return (
    <Card
      className="p-4 hover:bg-muted/50 transition-colors cursor-pointer active:scale-[0.98] touch-manipulation"
      onClick={handleClick}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          <div className="relative flex-shrink-0">
            <Avatar className="h-12 w-12">
              <AvatarImage src={userInfo.avatar} />
              <AvatarFallback className="bg-muted text-xs">
                {avatarFallback}
              </AvatarFallback>
            </Avatar>
            <div className="absolute -bottom-1 -right-1 bg-background rounded-full p-1 border border-border">
              {getIcon()}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-1">
              <p className="font-medium truncate pr-2">{safeName}</p>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <p className={`font-medium text-sm ${getAmountColor()}`}>
                  {getAmountPrefix()}
                  {fmt(transaction.amount)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {getTypeLabel()}
                </p>
                {rightSubLabel && transaction.status === "pending" &&
                  transaction.type !== "bill_split" && (
                    <p className="text-[11px] text-muted-foreground">
                      {rightSubLabel}
                    </p>
                  )}
              </div>
            </div>
            <p className="text-sm text-muted-foreground truncate leading-relaxed mb-1">
              {getSubtitle()}
            </p>
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {formatDate(transaction.date)}
              </p>
              <div className="flex items-center gap-2">
                {transaction.status === "pending" && (
                  <Badge variant="outline" className="text-xs px-2 py-0 h-5">
                    Pending
                  </Badge>
                )}
                {transaction.status === "failed" && (
                  <Badge
                    variant="destructive"
                    className="text-xs px-2 py-0 h-5"
                  >
                    Failed
                  </Badge>
                )}
                {extraRightText && (
                  <span className="text-xs text-muted-foreground">
                    {extraRightText}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
